import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { PasswordField } from '../components/PasswordField';
import { PasswordStrengthMeter } from '../components/PasswordStrengthMeter';
import { passwordStrength } from '../lib/passwordStrength';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Plane, ArrowLeft } from 'lucide-react';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => { document.title = 'Choose a new password | FlightDeals.in'; }, []);

  const strong = passwordStrength(pw).valid;
  const match = pw && pw === confirm;

  const submit = async (e) => {
    e.preventDefault();
    if (!strong) { toast.error('Password must be at least 6 characters.'); return; }
    if (!match) { toast.error('Passwords do not match.'); return; }
    setLoading(true);
    try {
      await api.resetPassword({ token, password: pw });
      toast.success('Password updated. Please sign in.');
      navigate('/login');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'This reset link is invalid or has expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-md mx-auto px-4 py-14">
        <div className="text-center mb-6">
          <span className="inline-grid place-items-center h-12 w-12 rounded-2xl bg-accent text-accent-foreground mb-3"><Plane className="h-6 w-6" /></span>
          <h1 className="font-display text-2xl font-semibold">Choose a new password</h1>
          <p className="text-sm text-muted-foreground">Enter a new password for your account.</p>
        </div>

        {!token ? (
          <div className="rounded-2xl border border-border bg-card p-5 text-center" data-testid="reset-no-token">
            <p className="font-medium">Invalid reset link</p>
            <p className="text-sm text-muted-foreground mt-1">This link is missing its token. Please request a new one.</p>
            <Link to="/forgot-password" className="text-accent text-sm inline-block mt-3">Request a new link</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3" data-testid="reset-form">
            <div>
              <Label>New password</Label>
              <PasswordField value={pw} onChange={(e) => setPw(e.target.value)} required minLength={6}
                autoComplete="new-password" testid="reset-password-input" placeholder="New password" />
              <PasswordStrengthMeter value={pw} testid="reset-strength" />
            </div>
            <div>
              <Label>Confirm password</Label>
              <PasswordField value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={6}
                autoComplete="new-password" testid="reset-confirm-input" placeholder="Re-enter password" />
              {confirm && !match && <p className="text-xs text-destructive mt-1" data-testid="reset-mismatch">Passwords do not match.</p>}
            </div>
            <Button type="submit" disabled={loading || !strong || !match} className="w-full h-11 rounded-xl bg-accent text-accent-foreground hover:bg-accent/90" data-testid="reset-submit-button">
              {loading ? 'Updating…' : 'Update password'}
            </Button>
          </form>
        )}

        <p className="text-center text-xs text-muted-foreground mt-6">
          <Link to="/login" className="text-accent inline-flex items-center gap-1"><ArrowLeft className="h-3 w-3" /> Back to sign in</Link>
        </p>
      </div>
    </Layout>
  );
}
